/**
 * PracticalSheetPage — Phase C édito (2026-09-21)
 * Page publique responsive d'une fiche pratique validée. Cartes/pictos,
 * identité ITERCATUS. i18n minimum FR+EN via t(). Le PDF n'est accessible
 * qu'aux adhérents actifs (contrôle serveur).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getPracticalSheetPublic,
  getPracticalSheetPdfUrl,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Clock, Ticket, MapPin, Compass, Accessibility, Backpack, Sparkles, Info,
  Star, FileDown, Loader2, AlertCircle, CalendarCheck,
} from "lucide-react";

const SECTIONS = [
  { key: "address", icon: MapPin, tKey: "practicalSheet.address" },
  { key: "how_to_get_there", icon: Compass, tKey: "practicalSheet.howToGetThere" },
  { key: "opening_hours", icon: Clock, tKey: "practicalSheet.openingHours" },
  { key: "pricing", icon: Ticket, tKey: "practicalSheet.pricing" },
  { key: "suggested_duration", icon: Clock, tKey: "practicalSheet.duration" },
  { key: "accessibility", icon: Accessibility, tKey: "practicalSheet.accessibility" },
  { key: "luggage_info", icon: Backpack, tKey: "practicalSheet.luggage" },
];

export default function PracticalSheetPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await getPracticalSheetPublic(slug);
        if (alive) setSheet(r.data);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("common.loading", "Chargement…")}
      </div>
    );
  }
  if (notFound || !sheet) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h1 className="font-heading text-2xl text-primary">{t("practicalSheet.notFoundTitle", "Fiche introuvable")}</h1>
        <p className="text-sm text-muted-foreground mt-2">
          {t("practicalSheet.notFoundBody", "Cette fiche n'existe pas ou n'est pas encore publiée.")}
        </p>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/">{t("common.backHome", "Retour à l'accueil")}</Link>
        </Button>
      </div>
    );
  }

  const isActiveMember = user?.membership_status === "active";
  const isAdmin = user && ["admin", "editor", "super_admin"].includes(user.role);
  const canDownload = isActiveMember || isAdmin;

  return (
    <article data-testid="practical-sheet-public-page" className="max-w-5xl mx-auto px-4 py-8 md:py-12">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-primary/70 font-medium">
          {t("practicalSheet.eyebrow", "Fiche pratique")}
        </div>
        <h1 className="font-heading text-3xl md:text-5xl text-primary mt-2">{sheet.title || "—"}</h1>
        {sheet.location_name && (
          <div className="mt-2 inline-flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4" /> <span className="text-sm">{sheet.location_name}</span>
          </div>
        )}
      </header>

      {sheet._cover_url && (
        <div className="rounded-2xl overflow-hidden border border-border shadow-sm mb-8">
          <img src={sheet._cover_url} alt={sheet.title || ""} className="w-full max-h-[420px] object-cover" />
        </div>
      )}

      {sheet.short_intro && (
        <p className="text-lg text-foreground/85 leading-relaxed mb-8 max-w-3xl">
          {sheet.short_intro}
        </p>
      )}

      {/* Cartes info pratique */}
      <section aria-labelledby="ps-infos" className="mb-10">
        <h2 id="ps-infos" className="font-heading text-xl text-primary mb-4 flex items-center gap-2">
          <Info className="h-5 w-5" /> {t("practicalSheet.infosTitle", "Informations pratiques")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SECTIONS.map(({ key, icon: Icon, tKey }) => {
            const val = sheet[key];
            if (!val) return null;
            return (
              <div
                key={key}
                className="rounded-xl border border-border bg-white p-4 flex items-start gap-3"
                data-testid={`ps-info-${key}`}
              >
                <div className="rounded-lg bg-primary/10 text-primary p-2 shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t(tKey)}</div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{val}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Incontournables */}
      {sheet.must_see && (
        <section aria-labelledby="ps-must" className="mb-10">
          <h2 id="ps-must" className="font-heading text-xl text-primary mb-3 flex items-center gap-2">
            <Star className="h-5 w-5" /> {t("practicalSheet.mustSee", "Incontournables")}
          </h2>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap">{sheet.must_see}</div>
        </section>
      )}

      {/* Conseils */}
      {sheet.practical_tips && (
        <section aria-labelledby="ps-tips" className="mb-10">
          <h2 id="ps-tips" className="font-heading text-xl text-primary mb-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5" /> {t("practicalSheet.tips", "Conseils pratiques")}
          </h2>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap">{sheet.practical_tips}</div>
        </section>
      )}

      {/* Bon à savoir */}
      {sheet.useful_info && (
        <section aria-labelledby="ps-useful" className="mb-10">
          <h2 id="ps-useful" className="font-heading text-xl text-primary mb-3 flex items-center gap-2">
            <Info className="h-5 w-5" /> {t("practicalSheet.useful", "Bon à savoir")}
          </h2>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap">{sheet.useful_info}</div>
        </section>
      )}

      {/* Photos d'illustration */}
      {Array.isArray(sheet._photos) && sheet._photos.length > 0 && (
        <section aria-labelledby="ps-photos" className="mb-10">
          <h2 id="ps-photos" className="font-heading text-xl text-primary mb-3">
            {t("practicalSheet.photos", "Photos")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {sheet._photos.map((p) => (
              <div key={p.id} className="rounded-xl overflow-hidden border border-border">
                {p.url && <img src={p.url} alt="" className="w-full h-40 md:h-48 object-cover" loading="lazy" />}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA PDF membre */}
      <section className="rounded-2xl bg-primary/5 border border-primary/20 p-6 flex items-start gap-4 flex-wrap">
        <div className="rounded-xl bg-primary/10 text-primary p-3">
          <FileDown className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading text-lg text-primary">{t("practicalSheet.memberPdfTitle", "Fiche PDF réservée aux adhérents")}</div>
          <p className="text-sm text-muted-foreground mt-1">
            {t("practicalSheet.memberPdfBody", "Téléchargez la fiche pratique au format A4 imprimable. Avantage exclusif de votre adhésion.")}
          </p>
        </div>
        {canDownload ? (
          <Button asChild data-testid="ps-download-pdf-btn">
            <a href={getPracticalSheetPdfUrl(sheet.slug)}>
              <FileDown className="h-4 w-4 mr-2" />
              {t("practicalSheet.downloadPdf", "Télécharger le PDF")}
            </a>
          </Button>
        ) : (
          <Button asChild variant="outline" data-testid="ps-join-cta-btn">
            <Link to="/adherer">{t("practicalSheet.joinCta", "Devenir adhérent")}</Link>
          </Button>
        )}
      </section>

      {sheet.last_verified && (
        <div className="mt-6 text-xs text-muted-foreground flex items-center gap-1">
          <CalendarCheck className="h-3.5 w-3.5" />
          {t("practicalSheet.lastVerified", "Dernière vérification")} :{" "}
          {new Date(sheet.last_verified).toLocaleDateString()}
        </div>
      )}
    </article>
  );
}
